/**
 * EntityListToolbar
 *
 * @deprecated Migrate callers to `src/components/collection/EntityListToolbar.tsx`.
 * Sessions, ProcessList, and TracksPage still use this legacy copy.
 *
 * A configurable toolbar for entity list pages (Cycles, Tracks, Sessions, etc.).
 * Same UX pattern as the item list Toolbar — search, filters, sort, group,
 * column visibility, density, view mode toggle.
 *
 * IMPORTANT: The outer wrapper must NOT have overflow-hidden or the absolute-
 * positioned dropdowns will be clipped and appear invisible.
 */

import { useState, useCallback, useRef } from 'react';
import {
  Search,
  SlidersHorizontal,
  Layers,
  ArrowUpDown,
  LayoutGrid,
  LayoutList,
  AlignJustify,
  ChevronDown,
  X,
  Columns3,
  Eye,
  EyeOff,
  GripVertical,
  Table2,
  CheckSquare,
  Square,
} from 'lucide-react';
import type { DensityMode, EntityFilterField } from '../../hooks/useEntityListState';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface EntitySortOption {
  field: string;
  label: string;
}

export interface EntityGroupOption {
  field: string;
  label: string;
}

export interface EntityColumnDef {
  key: string;
  label: string;
  defaultVisible: boolean;
}

interface EntityListToolbarProps {
  // Search
  searchQuery: string;
  onSearchChange: (q: string) => void;
  searchPlaceholder?: string;

  // Filters
  filterFields?: EntityFilterField[];
  activeFilters: Record<string, string[]>;
  onFilterChange: (field: string, values: string[]) => void;
  onClearFilters: () => void;
  filterCount: number;
  filterPanelOpen: boolean;
  onToggleFilterPanel: () => void;

  // Sort
  sort: { field: string; direction: 'asc' | 'desc' };
  onSortChange: (spec: { field: string; direction: 'asc' | 'desc' }) => void;
  sortOptions: EntitySortOption[];

  // Group
  groupBy: string | null;
  onGroupByChange: (field: string | null) => void;
  groupOptions?: EntityGroupOption[];

  // Columns
  allColumns: EntityColumnDef[];
  visibleColumns: string[];
  onToggleColumn: (key: string) => void;
  onReorderColumns: (order: string[]) => void;

  // Density
  density: DensityMode;
  onDensityChange: (d: DensityMode) => void;

  // View mode
  viewMode: 'list' | 'table';
  onViewModeChange: (mode: 'list' | 'table') => void;

  // Counts
  totalCount: number;
  filteredCount: number;
  selectedCount?: number;
}

// ─── Main component ───────────────────────────────────────────────────────────

export function EntityListToolbar({
  searchQuery,
  onSearchChange,
  searchPlaceholder = 'Search…',
  filterFields = [],
  activeFilters,
  onFilterChange,
  onClearFilters,
  filterCount,
  filterPanelOpen,
  onToggleFilterPanel,
  sort,
  onSortChange,
  sortOptions,
  groupBy,
  onGroupByChange,
  groupOptions = [],
  allColumns,
  visibleColumns,
  onToggleColumn,
  onReorderColumns,
  density,
  onDensityChange,
  viewMode,
  onViewModeChange,
  totalCount,
  filteredCount,
  selectedCount = 0,
}: EntityListToolbarProps) {
  const [sortOpen, setSortOpen] = useState(false);
  const [groupOpen, setGroupOpen] = useState(false);
  const [columnsOpen, setColumnsOpen] = useState(false);

  const closeAll = useCallback(() => {
    setSortOpen(false);
    setGroupOpen(false);
    setColumnsOpen(false);
  }, []);

  const currentSortLabel = sortOptions.find(o => o.field === sort.field)?.label ?? 'Sort';
  const currentGroupLabel = groupOptions.find(o => o.field === groupBy)?.label;

  return (
    <div className="space-y-0">
      {/* ── Main toolbar row ── */}
      {/* NOTE: no overflow-hidden here — dropdowns use absolute positioning */}
      <div className="border border-[var(--rule)] bg-[var(--bg-raised)] dark:border-[var(--rule)] dark:bg-[var(--bg-raised)]">
        {/* Search row */}
        <div className="flex items-center gap-2 border-b border-[var(--rule)] px-3 py-2 dark:border-[var(--rule)]">
          <Search className="h-3.5 w-3.5 shrink-0 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => onSearchChange(e.target.value)}
            placeholder={searchPlaceholder}
            className="min-w-0 flex-1 border-none bg-transparent text-sm text-[var(--text-sec)] placeholder-gray-400 outline-none dark:text-[var(--text-pri)] dark:placeholder-[var(--text-ter)]"
          />
          {searchQuery && (
            <button onClick={() => onSearchChange('')} className="rounded p-0.5 text-[var(--text-ter)] transition-colors hover:text-[var(--text-sec)] dark:text-[var(--text-ter)] dark:hover:text-[var(--text-sec)]">
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        {/* Controls row — no overflow-hidden so dropdowns can escape; wraps
            on a phone, where the controls do not fit one 390px row */}
        <div className="relative flex flex-wrap items-center gap-1.5 px-3 py-2">

          {/* Filters button */}
          {filterFields.length > 0 && (
            <ToolbarButton
              active={filterCount > 0 || filterPanelOpen}
              onClick={onToggleFilterPanel}
              icon={<SlidersHorizontal className="h-3.5 w-3.5" />}
              label="Filters"
              badge={filterCount > 0 ? filterCount : undefined}
            />
          )}

          {/* Sort dropdown */}
          <div className="relative">
            <ToolbarButton
              onClick={() => { closeAll(); setSortOpen(s => !s); }}
              icon={<ArrowUpDown className="h-3.5 w-3.5" />}
              label={currentSortLabel}
              suffix={sort.direction === 'asc' ? '↑' : '↓'}
              hasDropdown
            />
            {sortOpen && (
              <Dropdown onClose={() => setSortOpen(false)}>
                <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-[var(--text-ter)] dark:text-[var(--text-ter)]">Sort by</div>
                {sortOptions.map(opt => (
                  <DropdownItem
                    key={opt.field}
                    label={opt.label}
                    active={sort.field === opt.field}
                    suffix={sort.field === opt.field ? (sort.direction === 'asc' ? '↑ Asc' : '↓ Desc') : undefined}
                    onClick={() => {
                      const dir = sort.field === opt.field
                        ? sort.direction === 'asc' ? 'desc' : 'asc'
                        : 'desc';
                      onSortChange({ field: opt.field, direction: dir });
                      setSortOpen(false);
                    }}
                  />
                ))}
              </Dropdown>
            )}
          </div>

          {/* Group dropdown */}
          {groupOptions.length > 0 && (
            <div className="relative">
              <ToolbarButton
                active={!!groupBy}
                onClick={() => { closeAll(); setGroupOpen(g => !g); }}
                icon={<Layers className="h-3.5 w-3.5" />}
                label={currentGroupLabel ?? 'Group'}
                hasDropdown
              />
              {groupOpen && (
                <Dropdown onClose={() => setGroupOpen(false)}>
                  <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-[var(--text-ter)] dark:text-[var(--text-ter)]">Group by</div>
                  <DropdownItem label="No grouping" active={!groupBy} onClick={() => { onGroupByChange(null); setGroupOpen(false); }} />
                  <div className="mx-3 my-1 border-t border-[var(--rule)] dark:border-[var(--rule)]" />
                  {groupOptions.map(opt => (
                    <DropdownItem
                      key={opt.field}
                      label={opt.label}
                      active={groupBy === opt.field}
                      onClick={() => { onGroupByChange(opt.field); setGroupOpen(false); }}
                    />
                  ))}
                </Dropdown>
              )}
            </div>
          )}

          {/* Spacer */}
          <div className="flex-1" />

          {/* Result count */}
          <span className="text-xs tabular-nums text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            {selectedCount > 0
              ? <><span className="font-semibold text-primary-600 dark:text-primary-400">{selectedCount}</span> selected</>
              : filteredCount === totalCount
                ? <>{totalCount}</>
                : <><span className="font-semibold text-[var(--text-sec)] dark:text-[var(--text-sec)]">{filteredCount}</span> of {totalCount}</>
            }
          </span>

          <div className="mx-0.5 h-5 w-px bg-[var(--hover-tint)] dark:bg-[var(--rule)]" />

          {/* View mode toggle */}
          <div className="flex items-center bg-[var(--hover-tint)]/80 p-0.5 dark:bg-black/30">
            <button onClick={() => onViewModeChange('list')} className={` p-1 transition-all duration-150 ${viewMode === 'list' ? 'bg-[var(--bg-raised)] text-[var(--text-sec)] shadow-sm dark:bg-[var(--surface)] dark:text-[var(--text-pri)]' : 'text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:text-[var(--text-sec)] dark:hover:text-[var(--text-pri)]'}`} title="List view">
              <LayoutList className="h-3.5 w-3.5" />
            </button>
            <button onClick={() => onViewModeChange('table')} className={` p-1 transition-all duration-150 ${viewMode === 'table' ? 'bg-[var(--bg-raised)] text-[var(--text-sec)] shadow-sm dark:bg-[var(--surface)] dark:text-[var(--text-pri)]' : 'text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:text-[var(--text-sec)] dark:hover:text-[var(--text-pri)]'}`} title="Table view">
              <Table2 className="h-3.5 w-3.5" />
            </button>
          </div>

          {/* Column picker (table mode only) */}
          {viewMode === 'table' && allColumns.length > 0 && (
            <div className="relative">
              <button
                onClick={() => { closeAll(); setColumnsOpen(c => !c); }}
                className="p-1.5 text-[var(--text-ter)] transition-all duration-150 hover:bg-[var(--hover-tint)] hover:text-[var(--text-pri)] dark:text-[var(--text-sec)] dark:hover:bg-[var(--hover-tint)] dark:hover:text-[var(--text-pri)]"
                title="Column visibility"
              >
                <Columns3 className="h-4 w-4" />
              </button>
              {columnsOpen && (
                <ColumnPicker
                  allColumns={allColumns}
                  visibleColumns={visibleColumns}
                  onToggleColumn={onToggleColumn}
                  onReorderColumns={onReorderColumns}
                  onClose={() => setColumnsOpen(false)}
                />
              )}
            </div>
          )}

          {/* Density */}
          <div className="flex items-center bg-[var(--hover-tint)]/80 p-0.5 dark:bg-black/30">
            {(['compact', 'default', 'comfortable'] as DensityMode[]).map(mode => {
              const Icon = mode === 'compact' ? AlignJustify : mode === 'default' ? LayoutList : LayoutGrid;
              return (
                <button
                  key={mode}
                  onClick={() => onDensityChange(mode)}
                  className={` p-1 transition-all duration-150 ${density === mode ? 'bg-[var(--bg-raised)] text-[var(--text-sec)] shadow-sm dark:bg-[var(--surface)] dark:text-[var(--text-pri)]' : 'text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:text-[var(--text-sec)] dark:hover:text-[var(--text-pri)]'}`}
                  title={mode.charAt(0).toUpperCase() + mode.slice(1)}
                >
                  <Icon className="h-3.5 w-3.5" />
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* ── Filter panel (slides in below toolbar) ── */}
      {filterPanelOpen && filterFields.length > 0 && (
        <div className="mt-1 border border-[var(--rule)] bg-[var(--bg-raised)] p-4 dark:border-[var(--rule)] dark:bg-[var(--bg-raised)]">
          <div className="mb-3 flex items-center justify-between">
            <span className="text-sm font-semibold text-[var(--text-sec)] dark:text-[var(--text-pri)]">Filters</span>
            <div className="flex items-center gap-2">
              {filterCount > 0 && (
                <button onClick={onClearFilters} className="text-xs text-[var(--text-ter)] transition-colors hover:text-[var(--text-sec)] dark:text-[var(--text-sec)] dark:hover:text-[var(--text-pri)]">
                  Clear all
                </button>
              )}
              <button onClick={onToggleFilterPanel} className="rounded p-1 text-[var(--text-ter)] transition-colors hover:text-[var(--text-sec)] dark:text-[var(--text-ter)] dark:hover:text-[var(--text-sec)]">
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
            {filterFields.map(field => (
              <FilterField
                key={field.key}
                field={field}
                selectedValues={activeFilters[field.key] ?? []}
                onChange={values => onFilterChange(field.key, values)}
              />
            ))}
          </div>
        </div>
      )}

      {/* ── Active filter chips ── */}
      {filterCount > 0 && (
        <div className="flex items-center gap-2 flex-wrap pt-1">
          {Object.entries(activeFilters).flatMap(([field, values]) =>
            values.map(value => {
              const fieldDef = filterFields.find(f => f.key === field);
              const optionLabel = fieldDef?.options.find(o => o.value === value)?.label ?? value;
              return (
                <span
                  key={`${field}:${value}`}
                  className="inline-flex items-center gap-1.5 border border-primary-200 bg-primary-50 px-2 py-1 text-xs font-medium text-primary-700 dark:border-primary-800/50 dark:bg-primary-950/35 dark:text-primary-200"
                >
                  <span className="text-primary-400 dark:text-primary-500">{fieldDef?.label}:</span>
                  {optionLabel}
                  <button
                    onClick={() => {
                      const next = (activeFilters[field] ?? []).filter(v => v !== value);
                      onFilterChange(field, next);
                    }}
                    className="text-primary-400 transition-colors hover:text-primary-700 dark:text-primary-500 dark:hover:text-primary-300"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              );
            })
          )}
          <button onClick={onClearFilters} className="text-xs text-[var(--text-ter)] transition-colors hover:text-[var(--text-sec)] dark:text-[var(--text-ter)] dark:hover:text-[var(--text-sec)]">
            Clear all
          </button>
        </div>
      )}
    </div>
  );
}

// ─── FilterField — checkboxes for one filter field ────────────────────────────

function FilterField({
  field,
  selectedValues,
  onChange,
}: {
  field: EntityFilterField;
  selectedValues: string[];
  onChange: (values: string[]) => void;
}) {
  return (
    <div>
      <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">{field.label}</p>
      <div className="space-y-1">
        {field.options.map(opt => {
          const checked = selectedValues.includes(opt.value);
          return (
            <button
              key={opt.value}
              onClick={() => {
                const next = checked
                  ? selectedValues.filter(v => v !== opt.value)
                  : [...selectedValues, opt.value];
                onChange(next);
              }}
              className={`flex w-full items-center gap-2  px-2 py-1 text-left text-xs transition-colors ${
                checked
                  ? 'bg-primary-50 text-primary-700 dark:bg-primary-950/40 dark:text-primary-300'
                  : 'text-[var(--text-sec)] hover:bg-[var(--surface)] dark:text-[var(--text-sec)] dark:hover:bg-[var(--hover-tint)]'
              }`}
            >
              {checked
                ? <CheckSquare className="h-3.5 w-3.5 shrink-0 text-primary-600 dark:text-primary-400" />
                : <Square className="h-3.5 w-3.5 shrink-0 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />}
              {opt.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function ToolbarButton({
  active,
  onClick,
  icon,
  label,
  badge,
  suffix,
  hasDropdown,
}: {
  active?: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  badge?: number;
  suffix?: string;
  hasDropdown?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5  border px-2.5 py-1.5 text-xs font-medium transition-all duration-150 ${
        active
          ? 'border-primary-200 bg-primary-50 text-primary-700 shadow-sm dark:border-primary-800/60 dark:bg-primary-950/35 dark:text-primary-300'
          : 'border-[var(--rule)]/80 text-[var(--text-sec)] hover:border-[var(--rule)] hover:bg-[var(--surface)] dark:border-[var(--rule)] dark:text-[var(--text-sec)] dark:hover:bg-[var(--hover-tint)] dark:hover:text-[var(--text-pri)]'
      }`}
    >
      {icon}
      <span>{label}</span>
      {badge !== undefined && (
        <span className="min-w-[1.125rem] h-[1.125rem] flex items-center justify-center rounded-full bg-primary-600 text-white text-[10px] font-bold px-1">
          {badge}
        </span>
      )}
      {suffix && <span className="text-[10px] text-[var(--text-ter)] dark:text-[var(--text-ter)]">{suffix}</span>}
      {hasDropdown && <ChevronDown className="-mr-0.5 h-3 w-3 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />}
    </button>
  );
}

/** Dropdown panel — positioned absolutely, MUST have a `relative` ancestor. */
function Dropdown({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <>
      <div className="fixed inset-0 z-30" onClick={onClose} />
      <div className="absolute left-0 top-full z-40 mt-1 w-52 border border-[var(--rule)] bg-[var(--bg-raised)] py-1 shadow-xl dark:border-[var(--rule)] dark:bg-[var(--bg-raised)] dark:shadow-black/40">
        {children}
      </div>
    </>
  );
}

function DropdownItem({
  label,
  active,
  suffix,
  onClick,
}: {
  label: string;
  active?: boolean;
  suffix?: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center justify-between px-3 py-1.5 text-sm transition-colors ${
        active
          ? 'bg-primary-50/60 font-medium text-primary-700 dark:bg-primary-950/40 dark:text-primary-300'
          : 'text-[var(--text-sec)] hover:bg-[var(--surface)] dark:text-[var(--text-pri)] dark:hover:bg-[var(--hover-tint)]'
      }`}
    >
      <span>{label}</span>
      {suffix && <span className="text-[10px] font-medium text-[var(--text-ter)] dark:text-[var(--text-ter)]">{suffix}</span>}
    </button>
  );
}

function ColumnPicker({
  allColumns,
  visibleColumns,
  onToggleColumn,
  onReorderColumns,
  onClose,
}: {
  allColumns: EntityColumnDef[];
  visibleColumns: string[];
  onToggleColumn: (key: string) => void;
  onReorderColumns: (order: string[]) => void;
  onClose: () => void;
}) {
  const dragSrcIdx = useRef<number | null>(null);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);

  const visibleDefs = visibleColumns
    .map(key => allColumns.find(c => c.key === key))
    .filter(Boolean) as EntityColumnDef[];
  const hiddenCols = allColumns.filter(c => !visibleColumns.includes(c.key));

  const handleDrop = (e: React.DragEvent, idx: number) => {
    e.preventDefault();
    if (dragSrcIdx.current === null || dragSrcIdx.current === idx) return;
    const next = [...visibleColumns];
    const [moved] = next.splice(dragSrcIdx.current, 1);
    next.splice(idx, 0, moved);
    onReorderColumns(next);
    dragSrcIdx.current = null;
    setDragOverIdx(null);
  };

  return (
    <>
      <div className="fixed inset-0 z-30" onClick={onClose} />
      <div className="absolute right-0 top-full z-40 mt-1 w-60 overflow-hidden border border-[var(--rule)] bg-[var(--bg-raised)] shadow-xl dark:border-[var(--rule)] dark:bg-[var(--bg-raised)] dark:shadow-black/40">
        {visibleDefs.length > 0 && (
          <div className="py-1">
            <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-[var(--text-ter)] dark:text-[var(--text-ter)]">Visible — drag to reorder</div>
            {visibleDefs.map((col, idx) => (
              <div
                key={col.key}
                draggable
                onDragStart={() => { dragSrcIdx.current = idx; }}
                onDragOver={e => { e.preventDefault(); setDragOverIdx(idx); }}
                onDrop={e => handleDrop(e, idx)}
                onDragEnd={() => { dragSrcIdx.current = null; setDragOverIdx(null); }}
                className={`flex cursor-grab items-center gap-2 px-2 py-1.5 transition-colors dark:hover:bg-[var(--hover-tint)] ${dragOverIdx === idx ? 'border-t-2 border-primary-300 bg-primary-50 dark:border-primary-600 dark:bg-primary-950/30' : 'hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]'}`}
              >
                <GripVertical className="h-3.5 w-3.5 shrink-0 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
                <span className="flex-1 text-sm text-[var(--text-pri)] dark:text-[var(--text-pri)]">{col.label}</span>
                <button onClick={e => { e.stopPropagation(); onToggleColumn(col.key); }} className="rounded p-0.5 text-primary-500 transition-colors hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)]">
                  <Eye className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}
        {hiddenCols.length > 0 && (
          <div className={`py-1 ${visibleDefs.length > 0 ? 'border-t border-[var(--rule)] dark:border-[var(--rule)]' : ''}`}>
            <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-[var(--text-ter)] dark:text-[var(--text-ter)]">Hidden</div>
            {hiddenCols.map(col => (
              <button key={col.key} onClick={() => onToggleColumn(col.key)} className="flex w-full items-center gap-2 px-2 py-1.5 transition-colors hover:bg-[var(--surface)] dark:hover:bg-[var(--hover-tint)]">
                <EyeOff className="h-3.5 w-3.5 shrink-0 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
                <span className="flex-1 text-left text-sm text-[var(--text-ter)] dark:text-[var(--text-ter)]">{col.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
